import { motion } from 'framer-motion';
import { Volume2, Music2, Waves, Heart, ExternalLink, Star } from 'lucide-react';

const ALBUM_ARTWORK = "https://customer-assets.emergentagent.com/job_ip-character-hub/artifacts/0thjhhzk_Soundmoo_Volume1_3000x3000_Final.png";
const COSMIX_ARTWORK = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/p9m7qvbz_Cosmix%20Artwork.png";
const COSMIX_LOGO = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/nskmtkd0_Cosmix%20Logo.png";
const MOMO_LOGO = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/w9xja97m_MOMO%20Logo%20Intro%20TS.png";
const TRK_EVERYWHERE_ARTWORK = "https://customer-assets.emergentagent.com/job_c9ad24c8-9a40-422b-823a-575f7c88504d/artifacts/9w0yf8wu_Everywhere-art.jpg";
const TRK_LOGO = "https://customer-assets.emergentagent.com/job_c9ad24c8-9a40-422b-823a-575f7c88504d/artifacts/ouxmby5o_TRK%20FINAL%20LOGO%20full%20RES.png";
const HANGING_WITH_ME_ARTWORK = "https://customer-assets.emergentagent.com/job_c9ad24c8-9a40-422b-823a-575f7c88504d/artifacts/36lf83sq_01%20Hanging%20With%20Me%204.0.png";
const SPOTIFY_ALBUM_URL = "https://open.spotify.com/album/1MNkDqgLAi9ZVU69z92NvE";
const SPOTIFY_TRK_URL = "https://open.spotify.com/album/0m07LLsC3kLf2utJoJSCwz?si=a_Jz_JCmSZqFjNmJBDeNIw";
const SPOTIFY_HANGING_URL = "https://open.spotify.com/track/1l1zQxahmIRo58jZdyfeBV?si=e732e80e74cb42a5";
const SPOTIFY_COSMIX_URL = "https://open.spotify.com/track/16S6i3t313VeKNdhI1nWLj?si=8fcec74db2bd4dd9";

export const SoundIdentity = () => {
  return (
    <section
      id="sound-identity"
      data-testid="sound-identity-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="soundmoo-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.span className="text-sm font-medium tracking-widest uppercase text-[#FBBF24] mb-4 block">
            Music Identity
          </motion.span>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-5">
            Sonic Branding That Connects
          </h2>
          <p className="text-base text-white/80 leading-relaxed max-w-2xl mx-auto">
            We create music identities for both original IP and partner brands — 
            theme tunes, soundscapes, and audio DNA that enhances emotional connection 
            and creates lasting brand recall.
          </p>
        </motion.div>

        {/* NEW RELEASE - Hanging With Me */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <div className="glow-card glow-orange p-6 md:p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center relative z-10">
              {/* Album Artwork */}
              <motion.div
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
                  <img
                    src={HANGING_WITH_ME_ARTWORK}
                    alt="Hanging With Me - The Retro KiD x DJ KnoWan"
                    className="w-full h-auto"
                  />
                </div>
                {/* Out Now Badge */}
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-[#FF6B00] to-[#FBBF24] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  OUT NOW
                </div>
              </motion.div>

              {/* Release Info */}
              <div>
                {/* TRK Logo */}
                <img 
                  src={TRK_LOGO} 
                  alt="The Retro Kid" 
                  className="h-24 md:h-32 mb-4"
                />
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-3 py-1 rounded-full bg-[#FF6B00]/20 border border-[#FF6B00]/40 text-[#FF6B00] text-xs font-bold">
                    NEW RELEASE
                  </span>
                  <span className="px-3 py-1 rounded-full bg-[#FBBF24]/20 border border-[#FBBF24]/40 text-[#FBBF24] text-xs font-bold">
                    OUT NOW
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-2">
                  Hanging With Me
                </h3>
                <p className="text-white/60 text-sm mb-3">The Retro KiD x DJ KnoWan</p>
                <p className="text-white/80 leading-relaxed mb-6">
                  A bold Soundmoo release from The Retro KiD and DJ KnoWan, bringing character, 
                  attitude, and music identity together in one world.
                </p>

                {/* Release Date */}
                <div className="flex flex-wrap gap-3 mb-4">
                  <span className="px-4 py-2 rounded-full bg-[#FF6B00]/20 border border-[#FF6B00]/40 text-white text-sm font-medium">
                    Release Date: April 3, 2026
                  </span>
                </div>

                {/* Listen Button */}
                <motion.a
                  href={SPOTIFY_HANGING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#FBBF24] text-white font-semibold hover:opacity-90 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="hanging-listen-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  Listen on Spotify
                  <ExternalLink className="w-4 h-4" />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Cosmix Girlz - OUT NOW */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <div className="glow-card glow-pink p-6 md:p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center relative z-10">
              {/* Album Artwork */}
              <motion.div
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
                  <img
                    src={COSMIX_ARTWORK}
                    alt="Cosmix Girlz - Accessorize"
                    className="w-full h-auto"
                  />
                </div>
                {/* Out Now Badge */}
                <div className="absolute -top-3 -right-3 bg-[#FF69B4] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  OUT NOW
                </div>
              </motion.div>

              {/* Release Info */}
              <div>
                {/* Cosmix Girlz Logo */}
                <img 
                  src={COSMIX_LOGO} 
                  alt="Cosmix Girlz" 
                  className="h-32 md:h-40 mb-4"
                />
                <span className="text-xs font-medium tracking-widest uppercase text-[#FF69B4] mb-2 block">
                  Available Now
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-3">
                  Accessorize
                </h3>
                <p className="text-white/80 leading-relaxed mb-6">
                  The debut single from Cosmix Girlz is here! A fun, empowering anthem celebrating 
                  self-expression and girl power. Shine with the cosmic crew.
                </p>

                {/* Listen Now Button */}
                <motion.a
                  href={SPOTIFY_COSMIX_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF69B4] text-white font-semibold hover:bg-[#FF85C1] transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="cosmix-listen-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  Listen on Spotify
                  <ExternalLink className="w-4 h-4" />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Featured Album - Sleep & Emotional Comfort */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <div className="glow-card glow-purple p-6 md:p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center relative z-10">
              {/* Album Artwork */}
              <motion.div
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
                  <img
                    src={ALBUM_ARTWORK}
                    alt="Soundmoo Volume 1 - Sleep & Emotional Comfort"
                    className="w-full h-auto"
                  />
                </div>
                {/* New Release Badge */}
                <div className="absolute -top-3 -right-3 bg-[#FBBF24] text-[#0A1A3F] px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                  NEW RELEASE
                </div>
              </motion.div>

              {/* Album Info */}
              <div>
                {/* Momo Logo */}
                <img 
                  src={MOMO_LOGO} 
                  alt="Momo" 
                  className="h-32 md:h-40 mb-4"
                />
                <span className="text-xs font-medium tracking-widest uppercase text-[#FBBF24] mb-2 block">
                  First Album • Released November 29, 2025
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-3">
                  Sleep & Emotional Comfort
                </h3>
                <p className="text-white/80 leading-relaxed mb-6">
                  The debut album from Soundmoo featuring soothing soundscapes designed to help 
                  children relax, process emotions, and drift into peaceful sleep. Available on 
                  Spotify and all major streaming platforms.
                </p>

                {/* Available on streaming platforms */}
                <div className="flex flex-wrap gap-3 mb-4">
                  <span className="text-xs text-white/50">Available on:</span>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs">Spotify</span>
                    <span className="px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs">Apple Music</span>
                    <span className="px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs">YouTube Music</span>
                    <span className="px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs">Amazon Music</span>
                  </div>
                </div>

                {/* Listen Button */}
                <motion.a
                  href={SPOTIFY_ALBUM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1DB954] text-white font-semibold hover:bg-[#1ed760] transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="spotify-listen-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  Listen on Spotify
                  <ExternalLink className="w-4 h-4" />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* TRK - Everywhere Release */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <div className="glow-card glow-orange p-6 md:p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center relative z-10">
              {/* Album Artwork */}
              <motion.div
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
                  <img
                    src={TRK_EVERYWHERE_ARTWORK}
                    alt="The Retro Kid - Everywhere"
                    className="w-full h-auto"
                  />
                </div>
                {/* 200K Streams Badge */}
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-[#FF6B00] to-[#22C55E] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  200K+ PLAYS
                </div>
              </motion.div>

              {/* Album Info */}
              <div>
                {/* TRK Logo */}
                <img 
                  src={TRK_LOGO} 
                  alt="The Retro Kid" 
                  className="h-24 md:h-32 mb-4"
                />
                <span className="text-xs font-medium tracking-widest uppercase text-[#FF6B00] mb-2 block">
                  Featured Single • Over 200K Streams
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-3">
                  Everywhere (feat. TALIA)
                </h3>
                <p className="text-white/80 leading-relaxed mb-6">
                  A nostalgic anthem blending retro synths with modern production. "Everywhere" captures the 
                  essence of The Retro Kid's unique sound — perfect for licensing opportunities in lifestyle 
                  and entertainment.
                </p>

                {/* Streaming Stats */}
                <div className="flex flex-wrap gap-3 mb-4">
                  <span className="px-4 py-2 rounded-full bg-[#FF6B00]/20 border border-[#FF6B00]/40 text-white text-sm font-medium flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FF6B00]" />
                    200,000+ Streams
                  </span>
                  <span className="px-4 py-2 rounded-full bg-[#22C55E]/20 border border-[#22C55E]/40 text-white text-sm font-medium">
                    Released via XPLODED
                  </span>
                </div>

                {/* Listen Button */}
                <motion.a
                  href={SPOTIFY_TRK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#22C55E] text-white font-semibold hover:opacity-90 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="trk-listen-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  Listen on Spotify
                  <ExternalLink className="w-4 h-4" />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats and Benefits Row */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { icon: Music2, label: 'Soundscapes', value: '50+' },
                { icon: Heart, label: 'Themes', value: '12' },
                { icon: Waves, label: 'Formats', value: '8' },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-4 rounded-xl glow-card glow-cyan cursor-pointer"
                >
                  <stat.icon className="w-5 h-5 text-white/80 mx-auto mb-2 relative z-10" />
                  <div className="text-2xl font-bold text-white font-['Outfit'] relative z-10">{stat.value}</div>
                  <div className="text-xs text-white/60 relative z-10">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Why Sound Matters */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="glow-card glow-blue p-5 cursor-pointer">
              <div className="flex items-center gap-2 mb-4 relative z-10">
                <Volume2 className="w-5 h-5 text-white" />
                <h4 className="text-base font-semibold text-white">Why Sound Matters</h4>
              </div>
              <ul className="space-y-2 text-sm text-white/80 relative z-10">
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FBBF24]" />
                  Increases emotional connection by 40%
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FBBF24]" />
                  Enhances brand recall at retail
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FBBF24]" />
                  Creates multi-sensory play experiences
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FBBF24]" />
                  Supports sleep and emotional regulation
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
